import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, KeyRound, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { checkResetToken, resetPassword, claimBrowserForUser } from '../lib/authApi';
import { apiErrorMessage } from '../lib/projectApi';
import { WannaPublishLockup } from '../components/editor/WannaPublishLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';

/**
 * /reset-password?token=… — the page the password-reset email links to. Checks the link first (so an expired or used one
 * gets a clear message instead of a form that fails), then sets the new password; the backend signs this browser in and
 * signs every other device out.
 */
export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = new URLSearchParams(location.search).get('token') || '';
  const [check, setCheck] = useState(null); // null = loading, { valid, email, name }
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const apply = () => document.documentElement.classList.toggle('dark', !!mq?.matches);
    apply();
    mq?.addEventListener?.('change', apply);
    return () => mq?.removeEventListener?.('change', apply);
  }, []);

  useEffect(() => {
    let alive = true;
    if (!token) { setCheck({ valid: false }); return undefined; }
    checkResetToken(token).then((r) => { if (alive) setCheck(r); }).catch(() => { if (alive) setCheck({ valid: false }); });
    return () => { alive = false; };
  }, [token]);

  if (done) return <Navigate to="/app" replace />;

  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= 8 && confirm === password && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setBusy(true);
    try {
      const me = await resetPassword({ token, password });
      await claimBrowserForUser(me.user_id);
      setUser(me);
      toast.success('Password updated', { description: 'You are signed in here — every other device was signed out.', id: 'auth' });
      setDone(true);
    } catch (err) {
      setError(await apiErrorMessage(err, 'The password could not be changed.'));
    } finally {
      setBusy(false);
    }
  };

  const field = 'h-10 bg-background border-input text-sm';

  return (
    <main className="min-h-screen bg-[hsl(var(--wp-workspace))] text-foreground flex flex-col wp-landing" data-testid="reset-password-page">
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <Link to="/" className="flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="reset-brand-link">
          <WannaPublishLockup size={36} className="shrink-0" />
        </Link>
        <Button type="button" variant="ghost" size="sm" className="h-9 text-sm" onClick={() => navigate('/')} data-testid="reset-back-to-signin"><ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" />Back to sign in</Button>
      </header>

      <section className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-[420px]">
          <div className="mb-6">
            <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-card shadow-sm border border-border" aria-hidden="true"><KeyRound className="h-6 w-6 text-primary" /></div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight" style={{ fontFamily: 'Outfit, Inter, sans-serif' }} data-testid="reset-title">Choose a new password</h1>
            {check?.valid && <p className="mt-2 text-sm text-muted-foreground" data-testid="reset-for-email">For <span className="font-medium text-foreground">{check.email}</span>{check.name ? ` (${check.name})` : ''}.</p>}
          </div>

          <div className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-sm" data-testid="reset-card">
            {check === null && (
              <div className="space-y-3" aria-busy="true" data-testid="reset-loading">
                <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
                <div className="h-10 rounded bg-muted animate-pulse" />
                <div className="h-10 rounded bg-muted animate-pulse" />
              </div>
            )}

            {check && !check.valid && (
              <div className="space-y-4" data-testid="reset-invalid">
                <Alert className="border-border bg-background">
                  <AlertDescription className="text-sm flex items-start gap-2">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-primary mt-0.5" aria-hidden="true" />
                    <span>This reset link has expired or was already used. Links work once and for one hour.</span>
                  </AlertDescription>
                </Alert>
                <Button type="button" className="w-full h-11 text-sm font-semibold" onClick={() => navigate('/', { state: { forgot: true } })} data-testid="reset-request-new">Request a new link</Button>
              </div>
            )}

            {check?.valid && (
              <form onSubmit={submit} className="space-y-3.5" noValidate data-testid="reset-form">
                <div className="space-y-1.5">
                  <Label htmlFor="reset-password" className="text-xs">New password</Label>
                  <div className="relative">
                    <Input id="reset-password" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" placeholder="At least 8 characters" minLength={8} required autoFocus className={`${field} pr-10`} aria-invalid={!!error || undefined} data-testid="reset-password-input" />
                    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={showPw ? 'Hide password' : 'Show password'} data-testid="reset-password-toggle">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                  </div>
                  {password.length > 0 && password.length < 8 && <p className="text-[11px] text-muted-foreground" data-testid="reset-password-hint">{8 - password.length} more character{8 - password.length === 1 ? '' : 's'} to go.</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reset-confirm" className="text-xs">Confirm new password</Label>
                  <Input id="reset-confirm" type={showPw ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" placeholder="Type it again" required className={field} aria-invalid={mismatch || undefined} data-testid="reset-confirm-input" />
                  {mismatch && <p className="text-xs text-destructive" role="alert" data-testid="reset-mismatch">The two passwords do not match.</p>}
                </div>
                {error && <p role="alert" className="text-xs text-destructive" data-testid="reset-error">{error}</p>}
                <Button type="submit" className="w-full h-11 text-sm font-semibold" disabled={!canSubmit} data-testid="reset-submit-button">{busy ? 'Saving…' : 'Set new password and sign in'}</Button>
                <p className="text-[11px] text-muted-foreground">Changing the password signs you out everywhere else.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
